/**
 * What the tenant Audit tab is allowed to say, and from which fields.
 *
 * THE TAB WAS A THIN EVENT LIST. `audit_events` records twenty-two fields per
 * row — result, source, reason, correlation id, before/after values, actor
 * type, impersonation, retention class — and the tenant tab rendered five of
 * them with no way to open a row. An operator investigating "who repaired this
 * tenant, from where, and did it work" had to leave the tenant and search the
 * global audit page for it.
 *
 * NO SECOND STORE AND NO SECOND VOCABULARY. Everything here reads the canonical
 * document that `audit_log_service.log_action` writes; nothing is derived, and
 * no field is invented. Redaction already happened at the moment of
 * persistence (shared/audit/redaction.py), so what arrives is safe to show —
 * but only the fields named below are shown, because "safe" is not the same as
 * "worth putting in front of a person".
 */

export const VIEW = Object.freeze({
  LOADING: 'LOADING',
  ERROR: 'ERROR',
  EMPTY: 'EMPTY',
  READY: 'READY',
});

/**
 * "We could not load this" and "there is nothing here" are opposite facts.
 *
 * The tab rendered `events.length > 0 ? list : "No audit logs found"`, and its
 * fetch swallowed failures into an empty array — so a failed request told an
 * operator that a tenant had no audit history. On an audit surface that is
 * worse than on any other: absence of events is itself evidence, and this made
 * a network error indistinguishable from a clean record.
 *
 * Same rule, same order as the Licences panel: error outranks emptiness.
 */
export const resolveView = ({ loading, error, events }) => {
  if (loading) return VIEW.LOADING;
  if (error) return VIEW.ERROR;
  if (!events || events.length === 0) return VIEW.EMPTY;
  return VIEW.READY;
};

/**
 * THE TENANT SCOPE IS NOT OPTIONAL, and this refuses to build a query without
 * one rather than quietly asking for every tenant's audit history.
 *
 * `GET /audit-logs` is an operator endpoint: `audit:read` authorises reading
 * ACROSS tenants, which is correct for the global page and wrong for a panel
 * rendered inside one tenant. Dropping `tenant_id` here would not fail, would
 * not 403, and would return other tenants' events under this tenant's heading
 * — the worst shape of leak, because it looks like data about the tenant you
 * are looking at.
 *
 * Throwing is deliberate: a missing scope is a programming error, and the
 * alternative (returning a query without it) is the exact bug.
 */
export const tenantScopedQuery = ({ tenantId, filters = {}, skip = 0, limit = 25 }) => {
  if (!tenantId) {
    throw new Error('Refusing to query audit events without a tenant scope');
  }
  const params = { tenant_id: tenantId, skip, limit, sort: 'desc' };
  // Only filters the API actually implements. An unknown key would be dropped
  // server-side and the panel would show unfiltered results under a filter
  // chip, which reads as "there are no others".
  for (const key of ['action', 'actor_id', 'target_type', 'target_id', 'source', 'result',
                     'actor_type', 'start_date', 'end_date', 'search']) {
    const v = filters[key];
    if (v !== undefined && v !== null && String(v).trim() !== '') params[key] = v;
  }
  return params;
};

/** Every row's one-line summary. */
export const summaryOf = (event) => ({
  action: event?.action_description || event?.action || 'Unknown action',
  actor: event?.actor_email || event?.actor_id || null,
  target: event?.entity_name || event?.target_type || null,
  result: event?.result || null,
  timestamp: event?.timestamp || null,
});

//: Absent means "not recorded", which is a fact worth stating, and is NOT the
//: same as the field being inapplicable. Each entry says how to say so.
const NOT_RECORDED = Object.freeze({
  source: 'Not recorded',
  ip_address: 'Not recorded — no user request behind this event, or predates IP capture',
});

/**
 * The expandable detail, as an ordered list of stated fields.
 *
 * DELIBERATELY AN ALLOWLIST. The temptation on an audit surface is to render
 * whatever the document holds, since it is already redacted — but a stored
 * document is not a designed screen, and dumping it makes the two or three
 * fields that answer the operator's question compete with twenty that do not.
 * `details` stays available as the technical section, collapsed, exactly as
 * the global page treats it.
 */
export const detailRows = (event) => {
  if (!event) return [];
  const rows = [];
  const push = (key, label, value, opts = {}) => {
    if (value === undefined || value === null || value === '') {
      if (!NOT_RECORDED[key]) return;
      rows.push({ key, label, value: NOT_RECORDED[key], recorded: false, ...opts });
      return;
    }
    rows.push({ key, label, value: String(value), recorded: true, ...opts });
  };

  push('actor_email', 'Actor', event.actor_email);
  push('actor_type', 'Actor type', event.actor_type);
  push('actor_id', 'Actor ID', event.actor_id, { mono: true });
  push('target_type', 'Target type', event.target_type);
  push('entity_name', 'Entity', event.entity_name);
  push('target_id', 'Target ID', event.target_id, { mono: true });
  push('result', 'Result', event.result);
  // Rows written before the two writers converged carry the service name here
  // rather than a source; shown as unrecorded rather than as something an
  // operator could filter by. Same rule the global page already applies.
  push('source', 'Source',
       event.source && event.source !== 'cluvik-admin' ? String(event.source).replace(/_/g, ' ') : null);
  push('reason', 'Reason', event.reason);
  push('correlation_id', 'Correlation ID', event.correlation_id, { mono: true });
  push('module_name', 'Module', event.module_name);
  push('ip_address', 'IP address', event.ip_address, { mono: true });
  push('retention_class', 'Retention class', event.retention_class);
  return rows;
};

/**
 * An impersonated action has TWO identities and must never read as one.
 * Surfaced separately from the field list so it cannot be scrolled past.
 */
export const impersonationOf = (event) => (
  event?.impersonated_user_id
    ? { impersonatedUserId: String(event.impersonated_user_id) }
    : null
);

/** Whether this row has a before/after worth showing. */
export const hasChange = (event) => Boolean(event?.old_value || event?.new_value);
